import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { InboxesApi } from 'entities/Inbox'
import { type InboxLayerStore } from 'pages/settings/pages/inboxes/pages/id/store/inboxLayerStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { SelectAppAndNumbersStore } from 'pages/settings/ui/SelectAppAndNumbersCommon/store/SelectAppAndNumbersStore'

export enum AddNumberFromAppRoutes {
  SelectAppAndNumbers = 'select-app-and-numbers',
  Payment = 'payment',
}

const titleMap: Record<string, string> = {
  [AddNumberFromAppRoutes.SelectAppAndNumbers]: 'Select app & numbers',
  [AddNumberFromAppRoutes.Payment]: 'Payment',
}

export class AddNumberFromAppStore {
  private _currentStep = 1
  private _loading = false
  private _selectAppAndNumbersStore: SelectAppAndNumbersStore
  private readonly _paymentStore: PaymentCommonStore
  private _visibleUI = false

  constructor(private _inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    this._selectAppAndNumbersStore = new SelectAppAndNumbersStore(this._entities)
    this._paymentStore = new PaymentCommonStore(() => {}, this._entities)

    makeAutoObservable(this)
  }

  init = async () => {
    this._entities.complianceStore.loadNumberVendors()
    await this._entities.subscriptionStore.init()
  }

  get inboxLayerStore() {
    return this._inboxLayerStore
  }

  get selectAppAndNumbersStore() {
    return this._selectAppAndNumbersStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get loading() {
    return this._loading
  }

  get inboxId() {
    return this._inboxLayerStore.inboxId
  }

  get rootPath() {
    return `${InboxesPath.inboxNumbers(this.inboxId)}/${InboxesRoutes.addNumberFromApp}`
  }

  get availableLicensesCount() {
    return this._entities.subscriptionStore.licensesCount
  }

  get selectedNumbersCount() {
    return this.selectAppAndNumbersStore.selectedNumber ? 1 : 0
  }

  get requiredLicensesToBuy() {
    const numbersCount = this.selectedNumbersCount
    const availableLicenses = this.availableLicensesCount

    if (availableLicenses === 0) {
      return numbersCount
    }

    if (availableLicenses < numbersCount) {
      return numbersCount - availableLicenses
    }

    return 0
  }

  get stepMap() {
    return {
      1: AddNumberFromAppRoutes.SelectAppAndNumbers,
      2: AddNumberFromAppRoutes.Payment,
    }
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1
    return allSteps[currentPageIndex + direction]
  }

  getSettingsStep = (path: AddNumberFromAppRoutes) => {
    return this.getStepByPath(path)
  }

  getStepByPath = (routeStep: AddNumberFromAppRoutes) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === routeStep
    })
    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getNextStep}`,
    })
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getLastStep}`,
    })
  }

  redirectToNumbers = async () => {
    uiStore.changeRoute({
      path: InboxesPath.inboxNumbers(this.inboxId),
    })
  }

  buyNumber = async () => {
    this.paymentStore.setPaymentError(false)
    if (!this.inboxId) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const { selectedNumber, selectedNumberVendorId } = this._selectAppAndNumbersStore
      if (!selectedNumber || !selectedNumberVendorId) return

      await InboxesApi.assignAircallNumbersToInbox(this.inboxId, {
        numbers: [
          {
            vendor_id: selectedNumber.id.toString(),
            number: selectedNumber.formatted_number,
          },
        ],
        number_vendor_id: selectedNumberVendorId,
      })

      await this._inboxLayerStore.init(this.inboxId)

      toastStore.add({
        type: 'success',
        title: 'Number added',
      })

      if (this._visibleUI) {
        this.redirectToNumbers()
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Failed to add number',
      })

      if (e instanceof AxiosError && e.errors.getError('stripe') === 'Your card was declined.') {
        this.paymentStore.setPaymentError(true)
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose() {
    this._selectAppAndNumbersStore.reset()
  }
}
